import { Outlet, Link, createRootRoute, HeadContent, Scripts, useRouterState, redirect } from "@tanstack/react-router";
import { useEffect } from "react";
import { resolveLegacyRedirect } from "@/lib/legacyRedirects";

import appCss from "../styles.css?url";
import { QuoteCartProvider } from "@/lib/quote-cart";
import { QuoteCartButton, QuoteCartDrawer } from "@/components/QuoteCart";
import { MobileCallButton } from "@/components/MobileCallButton";
import { FinancingCTA } from "@/components/FinancingCTA";
import { absUrl, SITE_URL } from "@/lib/seo";
import { businessHeadScripts } from "@/lib/businessJsonLd";
import ogDefault from "@/assets/original/hero-shade.jpg";

const DEFAULT_OG_IMAGE = absUrl(ogDefault);

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  beforeLoad: ({ location }) => {
    const target = resolveLegacyRedirect(location.pathname);
    if (target) {
      throw redirect({ to: target, replace: true, statusCode: 301 });
    }
  },
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Texas Made Shade & Covers — Custom Awnings, Covers & Welding" },
      { name: "description", content: "Custom-built awnings, covers, sail shades, curtains, marine canvas and aluminum welding. Built in our Seguin shop and installed across Texas for homes and businesses for almost 30 years." },
      // <meta name="keywords"> intentionally omitted — ignored by Google
      // since ~2009, and stuffed values can look spammy to other crawlers.
      { name: "author", content: "Texas Made Shade & Covers" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
      { name: "googlebot", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
      { name: "googlebot-news", content: "index, follow" },
      { name: "google", content: "notranslate" },
      
      { name: "bingbot", content: "index, follow, max-image-preview:large, max-snippet:-1" },
      { name: "slurp", content: "index, follow" },
      { name: "duckduckbot", content: "index, follow" },
      { name: "yandex", content: "index, follow" },
      { name: "yandexbot", content: "index, follow" },
      { name: "applebot", content: "index, follow" },
      { name: "facebookexternalhit", content: "index, follow" },
      { name: "twitterbot", content: "index, follow" },
      { name: "AdsBot-Google", content: "index, follow" },
      { name: "rating", content: "general" },
      { name: "revisit-after", content: "7 days" },
      { name: "referrer", content: "strict-origin-when-cross-origin" },
      { property: "og:site_name", content: "Texas Made Shade & Covers" },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_US" },
      { property: "og:title", content: "Texas Made Shade & Covers — Custom Awnings, Covers & Welding" },
      { property: "og:description", content: "Custom outdoor shade structure installation in Texas. Serving residential and commercial projects in San Antonio, New Braunfels, and beyond. Get a free estimate." },
      { property: "og:image", content: DEFAULT_OG_IMAGE },
      { property: "og:image:secure_url", content: DEFAULT_OG_IMAGE },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "Texas Made Shade & Covers — custom awnings and shade structures across Texas" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@texasmadeshade" },
      { name: "twitter:title", content: "Texas Made Shade & Covers — Custom Awnings, Covers & Welding" },
      { name: "twitter:description", content: "Custom outdoor shade structure installation in Texas. Serving residential and commercial projects in San Antonio, New Braunfels, and beyond. Get a free estimate." },
      { name: "twitter:image", content: DEFAULT_OG_IMAGE },
      { name: "twitter:image:alt", content: "Texas Made Shade & Covers — custom awnings and shade structures across Texas" },
      { name: "subject", content: "Custom outdoor shade structure installation in Texas. Serving residential and commercial projects in San Antonio, New Braunfels, and beyond. Get a free estimate." },
      { name: "theme-color", content: "#0b1320" },
      // Geo / local-SEO targeting (applies to every page)
      { name: "geo.region", content: "US-TX" },
      { name: "geo.placename", content: "San Antonio, Seguin, Austin, San Marcos, New Braunfels, Victoria, Corpus Christi, Laredo, Beeville, Junction, Uvalde, Castroville" },
      { name: "geo.position", content: "29.5688;-97.9647" },
      { name: "ICBM", content: "29.5688, -97.9647" },
      { name: "location", content: "Serving San Antonio, Seguin, Austin, San Marcos, New Braunfels, Victoria, Corpus Christi, Laredo, Beeville, Junction, Uvalde, Castroville and surrounding Central, South, and Coastal Texas areas." },
      { name: "coverage", content: "Texas" },
      { name: "distribution", content: "local" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "preload", as: "font", type: "font/woff2", href: "/fonts/oswald-latin.woff2", crossOrigin: "anonymous" },
      // NOTE: Do NOT set a site-wide canonical here — it would point every
      // page at the homepage and Google flags every other URL as
      // "Alternate page with proper canonical tag" (i.e. de-indexed).
      // Each route sets its own og:url via routeMeta(), and Google falls
      // back to the page's own URL as canonical when no <link> is present.
      { rel: "sitemap", type: "application/xml", href: "/sitemap.xml" },
    ],
    scripts: [
      // Google Analytics 4 (gtag.js) — loaded site-wide on every route
      { src: "https://www.googletagmanager.com/gtag/js?id=G-HHPJESTBPH", async: true },
      {
        children:
          "window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','G-HHPJESTBPH',{send_page_view:true});",
      },
      // Canonical Organization + LocalBusiness identity (NAP, geo, sameAs)
      ...businessHeadScripts(),

    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isSnapshotPreview = pathname === "/vertical-shades-lp";
  // Init attribution + log page views as the user navigates between routes.
  useEffect(() => {
    let cancelled = false;
    void import("@/lib/analytics").then(({ initAttribution, track }) => {
      if (cancelled) return;
      initAttribution();
      track("page_view");
    });
    // Send GA4 page_view on every client-side route change
    if (typeof window !== "undefined" && typeof (window as any).gtag === "function") {
      (window as any).gtag("event", "page_view", {
        page_path: pathname,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
    return () => {
      cancelled = true;
    };
  }, [pathname]);

  // Phone number behavior: on touch devices (mobile/tablet) tel: links remain
  // tappable to start a call. On non-touch desktops, neutralize the link so the
  // number is shown as plain text — clicking does nothing and there is no href.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const isTouch =
      window.matchMedia?.("(hover: none), (pointer: coarse)")?.matches ||
      "ontouchstart" in window ||
      (navigator as any).maxTouchPoints > 0;
    if (isTouch) return;
    const SPOKEN = "Phone number: +1 (830) 390-9588";
    const neutralize = () => {
      document.querySelectorAll<HTMLAnchorElement>('a[href^="tel:"]').forEach((a) => {
        if (a.dataset.telNeutralized === "1") return;
        a.dataset.telNeutralized = "1";
        a.removeAttribute("href");
        // Strip link semantics so AT doesn't announce a non-functional link.
        a.setAttribute("role", "text");
        a.removeAttribute("aria-disabled");
        a.setAttribute("tabindex", "-1");
        // Always give SR users the spoken phone number — covers icon-only buttons
        // (e.g. the round mobile-call icon) that have no visible digits.
        a.setAttribute("aria-label", SPOKEN);
        a.style.cursor = "default";
        a.style.textDecoration = "none";
        // Visually-hidden fallback so the number is also exposed via the
        // accessible name calculation when role="text" is unsupported.
        if (!a.querySelector("[data-tel-sr-only]")) {
          const sr = document.createElement("span");
          sr.dataset.telSrOnly = "1";
          sr.textContent = SPOKEN;
          sr.style.cssText =
            "position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;";
          a.appendChild(sr);
        }
        a.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
        });
      });
    };
    neutralize();
    const mo = new MutationObserver(() => neutralize());
    mo.observe(document.body, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, [pathname]);

  // Global delegated click tracking for outbound links, internal nav links,
  // and tel: links (mobile/tablet only — desktop neutralizes tel: above).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onClick = (e: MouseEvent) => {
      const a = (e.target as HTMLElement | null)?.closest?.("a") as HTMLAnchorElement | null;
      if (!a) return;
      const href = a.getAttribute("href") || "";
      if (!href || href.startsWith("#") || href.startsWith("javascript:")) return;

      const text = (a.innerText || a.getAttribute("aria-label") || "").trim().slice(0, 80);
      const navRegion = a.closest("header,nav,footer")?.tagName?.toLowerCase() || "body";

      // tel: — fire first-party + GA4 tracking. Dedupe so we only fire once per click
      // even if a component handler also calls track() (sets data-tel-tracked).
      if (/^tel:/i.test(href)) {
        if (a.dataset.telTracked === "1") return;
        a.dataset.telTracked = "1";
        // Reset the marker on the next tick so a second tap also tracks.
        setTimeout(() => { delete a.dataset.telTracked; }, 1500);
        const phone = href.replace(/^tel:/i, "").replace(/[^\d+]/g, "");
        const placement =
          a.dataset.ctaPlacement ||
          `${navRegion}_${window.location.pathname.replace(/^\//, "") || "home"}`;
        void import("@/lib/analytics").then(({ track }) => {
          track("phone_call_click", {
            target: phone,
            metadata: { placement, link_text: text, page_path: window.location.pathname },
          });
        });
        return;
      }

      const gtag = (window as any).gtag;
      if (typeof gtag !== "function") return;

      // mailto: / sms: — already covered by component-level track() calls.
      if (/^(mailto|sms):/i.test(href)) return;

      let url: URL | null = null;
      try { url = new URL(href, window.location.origin); } catch { return; }
      const isOutbound = url.host && url.host !== window.location.host;

      if (isOutbound) {
        gtag("event", "click_outbound", {
          event_category: "outbound",
          event_label: url.href,
          link_domain: url.host,
          link_url: url.href,
          link_text: text,
          nav_region: navRegion,
        });
      } else {
        gtag("event", "click_nav", {
          event_category: "navigation",
          event_label: url.pathname,
          link_url: url.pathname + url.search,
          link_text: text,
          nav_region: navRegion,
        });
      }
    };
    document.addEventListener("click", onClick, { capture: true });
    return () => document.removeEventListener("click", onClick, { capture: true } as any);
  }, []);

  return (
    <QuoteCartProvider>
      {!isSnapshotPreview && <a href="#main-content" className="skip-link">Skip to main content</a>}
      <Outlet />
      {!isSnapshotPreview && (
        <>
          <QuoteCartButton />
          <QuoteCartDrawer />
          <FinancingCTA />
          <MobileCallButton />
        </>
      )}
    </QuoteCartProvider>
  );
}
